// components/BaseTable/BaseTable.tsx
import CommonModal from "@components/common/CommonModal";
import { useUser } from "@/context/useUser";
import { UserPreferencesService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { BaseTableProps } from "@/utils/types/baseTable";
import { ColumnState } from "@/utils/types/userPreferencesGrid";
import { ProTable } from "@ant-design/pro-components";
import { Input, TableProps } from "antd";
import { useEffect, useMemo, useState } from "react";

function normalizeSize(v: unknown): TableProps["size"] {
  if (v === "default" || v === "large") {
    return "large";
  }
  if (v === "compact" || v === "small") {
    return "small";
  }
  return "middle";
}

const BaseTable = <T extends object>({
  loading,
  columns,
  dataSource,
  request,
  page,
  pageSize,
  total,
  onPageChange,
  headerTitle,
  rowKey,
  toolBarRender,
  scroll = {},
  search = false,
  manualSearch = false,
  searchText = "",
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder,
  showModal = false,
  modalTitle = "",
  modalContent,
  options = true,
  onModalClose,
  tableName,
  showPagination = true,
}: BaseTableProps<T>) => {
  const paginationConfig = {
    current: page,
    pageSize,
    total,
    onChange: onPageChange,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total}`,
    showSizeChanger: true,
    showQuickJumper: true,
  };

  const { preferencesData, fetchPreferencesData } = useUser();
  const [columnsState, setColumnsState] = useState<ColumnState>({});
  const [size, setSize] = useState<TableProps["size"]>("middle");

  const defaultColumnsState = useMemo(() => {
    const state: ColumnState = {};
    columns.forEach(col => {
      if (col.dataIndex) {
        state[col.dataIndex as string] = { show: true }; // default show true
      }
    });
    return state;
  }, [columns]);

  useEffect(() => {
    if (preferencesData && preferencesData.length > 0) {
      const colPref = preferencesData.find(item => item.key === tableName);
      setColumnsState((colPref?.json as ColumnState) ?? {});
      const saved = preferencesData.find(
        item => item.key === gridName.settingsTable
      )?.json;
      setSize(normalizeSize(saved));
    }
  }, [preferencesData, tableName]);

  const handleColumnsStateChange = async (newState: ColumnState) => {
    setColumnsState(newState);
    if (tableName) {
      const payload = {
        key: tableName,
        json:
          Object.keys(newState).length === 0 ? defaultColumnsState : newState,
      };
      await UserPreferencesService.addUserPreferencesGrid(payload);
      fetchPreferencesData();
    }
  };

  async function handleChangeSize(v: unknown) {
    const normalized = normalizeSize(v);
    setSize(normalized);

    // Save regardless of tableName – density is a global preference
    await UserPreferencesService.addUserPreferencesGrid({
      key: gridName.settingsTable,
      json: normalized,
    });
    fetchPreferencesData();
  }

  const proTableSearch = search && !manualSearch ? {} : false;

  // Compose a single toolbar with global search (if enabled) + caller's items
  const internalToolBarRender = () => {
    const items: React.ReactNode[] = [];

    // Show manual search only when manualSearch is true
    if (manualSearch) {
      items.push(
        <Input.Search
          key="global-search"
          allowClear
          width={100}
          size="large"
          placeholder={searchPlaceholder ?? "Search…"}
          value={searchText}
          onChange={e => onSearchChange?.(e.target.value)}
          onSearch={val => onSearchSubmit?.(val ?? "")}
        />
      );
    }

    const extra = toolBarRender?.() ?? [];
    return [...items, ...extra];
  };
  return (
    <>
      <ProTable<T>
        rowKey={rowKey}
        rowSelection={false}
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        search={proTableSearch}
        pagination={showPagination ? paginationConfig : false}
        toolBarRender={internalToolBarRender}
        columnsState={{
          value: columnsState,
          onChange: handleColumnsStateChange,
        }}
        size={size}
        onSizeChange={handleChangeSize}
        scroll={scroll}
        headerTitle={headerTitle}
        options={options}
        request={request}
      />
      {showModal && (
        <CommonModal
          open={showModal}
          title={modalTitle}
          onCancel={onModalClose ?? (() => {})}
        >
          {modalContent}
        </CommonModal>
      )}
    </>
  );
};

export default BaseTable;
